import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import MultiSelectWithCheckbox, {OptionNode} from 'shared/ui/Input/MultiSelectWithCheckbox/MultiSelectWithCheckbox';

import {useMonitorPicker} from '../../model/monitorPicker';

import MonitorOrderList from './MonitorOrderList/MonitorOrderList';

import styles from './MonitorSelectField.module.scss';

const DEFAULT_MAX = 15;

interface IMonitorLabelResolverProps {
    uuid: string;
    /* eslint-disable-next-line no-unused-vars */
    onResolved: (uuid: string, label: string) => void;
}

const MonitorLabelResolver: React.FC<IMonitorLabelResolverProps> = ({uuid, onResolved}) => {
    const {useLabel} = useMonitorPicker();
    const label = useLabel(uuid);

    useEffect(() => {
        if (label) {
            onResolved(uuid, label);
        }
    }, [label, uuid, onResolved]);

    return null;
};

interface IMonitorSelectFieldProps {
    value: string[];
    /* eslint-disable-next-line no-unused-vars */
    onChange: (ids: string[]) => void;
    initialLabels?: Record<string, string>;
    error?: string;
    maxCount?: number;
}

const MonitorSelectField: React.FC<IMonitorSelectFieldProps> = ({
    value,
    onChange,
    initialLabels,
    error,
    maxCount = DEFAULT_MAX,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const searchTimer = useRef<ReturnType<typeof setTimeout>>();
    const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>({});

    const {useSearch} = useMonitorPicker();
    const searchResults = useSearch(searchTerm);

    const searchLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        searchResults.forEach((m) => {
            labels[m.uuid] = m.label;
        });
        return labels;
    }, [searchResults]);

    const labelCache = useMemo(
        () => ({...(initialLabels ?? {}), ...resolvedLabels, ...searchLabels}),
        [initialLabels, resolvedLabels, searchLabels],
    );

    const handleResolved = useCallback((uuid: string, label: string) => {
        setResolvedLabels((prev) => (prev[uuid] === label ? prev : {...prev, [uuid]: label}));
    }, []);

    const handleSearch = useCallback((query: string) => {
        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }
        searchTimer.current = setTimeout(() => setSearchTerm(query.trim()), 400);
    }, []);

    const missingSelected = value.filter((u) => !labelCache[u]);

    const monitorOptions: OptionNode[] = useMemo(() => {
        const results: OptionNode[] = searchResults.map((m) => ({
            id: m.uuid,
            value: m.uuid,
            label: m.label,
        }));
        const inResults = new Set(results.map((o) => o.id));
        const selectedOnly: OptionNode[] = value
            .filter((u) => !inResults.has(u))
            .map((u) => ({id: u, value: u, label: labelCache[u] ?? u}));
        return [...selectedOnly, ...results];
    }, [searchResults, value, labelCache]);

    const orderItems = useMemo(
        () => value.map((id) => ({id, label: labelCache[id] ?? id})),
        [value, labelCache],
    );

    const handleReorder = (ids: string[]) => onChange(ids);
    const handleRemove = (id: string) => onChange(value.filter((x) => x !== id));

    return (
        <>
            {missingSelected.map((u) => (
                <MonitorLabelResolver key={u} uuid={u} onResolved={handleResolved}/>
            ))}

            <ModalBodyWrapper className={styles.section}>
                <span className={styles.sectionTitle}>{t`Мониторы`}</span>
                <FormGroupWithError error={error} className={styles.formGroup}>
                    <MultiSelectWithCheckbox
                        options={monitorOptions}
                        value={value}
                        onChange={onChange}
                        placeholder={t`Мониторы`}
                        searchable
                        onSearch={handleSearch}
                        searchPlaceholder={t`Поиск по мониторам`}
                        maxCount={maxCount}
                    />
                </FormGroupWithError>
            </ModalBodyWrapper>

            <ModalBodyWrapper className={styles.section}>
                <span className={styles.sectionTitle}>{t`Порядок отображения`}</span>
                {orderItems.length === 0 ? (
                    <span className={styles.emptyOrder}>{t`Не выбран ни один монитор`}</span>
                ) : (
                    <MonitorOrderList
                        items={orderItems}
                        onReorder={handleReorder}
                        onRemove={handleRemove}
                    />
                )}
            </ModalBodyWrapper>
        </>
    );
};

export default MonitorSelectField;
